import React, { useEffect, useMemo, useState } from 'react'
import bcrypt from 'bcryptjs'
import Swal from 'sweetalert2'
import { supabase } from '../supabaseClient'
import Header from '../components/Header'
import { useBarista } from '../contexts/BaristaContext'
import { authHeaders } from '../utils/authFetch'

/* 
const PAYMENT_METHODS = [
    { id: 'cash', label: 'Cash' },
    { id: 'gcash', label: 'GCash' },
    { id: 'maya', label: 'Maya' },
    { id: 'card', label: 'Card' },
    { id: 'qrph', label: 'QRPh' }
]
*/

export default function Settings2() {
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [currentStaff, setCurrentStaff] = useState(null)

    const { selectedBarista } = useBarista()

    const [formData, setFormData] = useState({
        newPin: '',
        confirmPin: ''
    })

    useEffect(() => {
        fetchCurrentStaff()
    }, [])

    const fetchCurrentStaff = async () => {
        try {
            setLoading(true)

            const userEmail = sessionStorage.getItem('userEmail') || localStorage.getItem('userEmail')
            const userType = sessionStorage.getItem('userType') || localStorage.getItem('userType')

            if (!userEmail || !userType) {
                throw new Error('User session not found.')
            }

            if (userType === 'admin') {
                const { data, error } = await supabase
                    .from('admin_accounts')
                    .select('id, username, email')
                    .eq('email', userEmail)
                    .single()

                if (error) throw error

                setCurrentStaff({ ...data, role: 'admin', userType: 'admin' })
                return
            }

            if (userType === 'staff') {
                const { data, error } = await supabase
                    .from('staff')
                    .select('id, first_name, last_name, email, company_email, role')
                    .or(`email.eq.${userEmail},company_email.eq.${userEmail}`)
                    .maybeSingle()

                if (error) throw error
                if (!data) throw new Error('Staff account not found.')

                setCurrentStaff({ ...data, userType: 'staff' })
                return
            }

            setCurrentStaff({ role: 'Non-Staff' })
        } catch (error) {
            console.error('Error fetching settings context:', error)
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Failed to load settings.',
                confirmButtonColor: '#FF6F3C'
            })
        } finally {
            setLoading(false)
        }
    }

    const isAdminStaff = useMemo(() => {
        const role = String(currentStaff?.role || '').trim().toLowerCase()
        return role === 'admin' || currentStaff?.userType === 'admin'
    }, [currentStaff])

    /* 
    const [branches, setBranches] = useState([])
    const [selectedBranchId, setSelectedBranchId] = useState('')
    const [branchPaymentMethods, setBranchPaymentMethods] = useState([])
    const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false)
    const [savingPaymentMethods, setSavingPaymentMethods] = useState(false)

    useEffect(() => {
        if (isAdminStaff) fetchBranches()
    }, [isAdminStaff])

    useEffect(() => {
        if (selectedBranchId) {
            fetchBranchPaymentMethods(selectedBranchId)
        } else {
            setBranchPaymentMethods([])
        }
    }, [selectedBranchId])
    */

    const isBaristaAccount = useMemo(() => {
        return String(currentStaff?.role || '').trim().toLowerCase() === 'barista'
    }, [currentStaff])

    const handleInputChange = (e) => {
        const { name, value } = e.target
        let nextValue = value
        if (name === 'newPin' || name === 'confirmPin') {
            nextValue = value.replace(/\D/g, '').slice(0, 6)
        }
        setFormData((prev) => ({ ...prev, [name]: nextValue }))
    }

    const validateForm = () => {
        if (!formData.newPin.trim()) return 'New PIN is required.'
        if (!/^\d{4,6}$/.test(formData.newPin)) return 'PIN must be 4 to 6 digits.'
        if (!formData.confirmPin.trim()) return 'Please confirm the new PIN.'
        if (formData.newPin !== formData.confirmPin) return 'PIN confirmation does not match.'
        return null
    }


    const isSavingPinRef = React.useRef(false)

    // ── Admin OTP helper: sends a verification code to the admin's registered email
    // before the PIN is written. Layout mirrors the Login OTP step, but colors match
    // the light Settings theme (white boxes, gray border, orange accent/focus). ──
    const requestAndVerifyAdminOtp = async (admin) => {
        const generateOtpCode = () => Math.floor(100000 + Math.random() * 900000).toString()

        const sendOnce = async () => {
            const code = generateOtpCode()
            const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString()

            const { error: insertError } = await supabase
                .from('admin_otp_codes')
                .insert({ email: admin.email, otp: code, user_type: 'admin', expires_at: expiresAt })
            if (insertError) throw new Error(insertError.message || 'Failed to generate code')

            const { error: emailError } = await supabase.functions.invoke('send-otp-email-v2', {
                body: { email: admin.email, otp: code }
            })
            if (emailError) throw new Error('Failed to send verification email. Please try again.')
        }

        await sendOnce()

        while (true) {
            const { value: otp, isConfirmed, isDenied } = await Swal.fire({
                title: 'Enter Verification Code',
                text: `We sent a 6-digit code to ${admin.email}.`,
                html: `
                    <div id="admin-otp-container" style="display:flex; gap:8px; justify-content:center; margin-top:12px;">
                        ${Array.from({ length: 6 }).map((_, i) => `
                            <input
                                id="admin-otp-box-${i}"
                                type="text"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                maxlength="1"
                                autocomplete="one-time-code"
                                style="width:48px; height:56px; text-align:center; font-size:24px; font-weight:bold; border:2px solid #d1d5db; border-radius:8px; outline:none; color:#1f2937; background:#ffffff;"
                            />
                        `).join('')}
                    </div>
                `,
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: 'Verify',
                denyButtonText: 'Resend Code',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#FF6F3C',
                denyButtonColor: '#6B7280',
                background: '#ffffff',
                didOpen: () => {
                    const boxes = Array.from({ length: 6 }, (_, i) => document.getElementById(`admin-otp-box-${i}`))
                    boxes[0]?.focus()

                    boxes.forEach((box, i) => {
                        box.addEventListener('focus', () => { box.style.borderColor = '#FF6F3C' })
                        box.addEventListener('blur', () => { box.style.borderColor = '#d1d5db' })
                        box.addEventListener('input', (e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 1)
                            e.target.value = val
                            if (val && i < 5) boxes[i + 1]?.focus()
                        })
                        box.addEventListener('keydown', (e) => {
                            if (e.key === 'Backspace' && !box.value && i > 0) {
                                boxes[i - 1]?.focus()
                            }
                            if (e.key === 'Enter') {
                                Swal.clickConfirm()
                            }
                        })
                        box.addEventListener('paste', (e) => {
                            e.preventDefault()
                            const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6)
                            pasted.split('').forEach((digit, idx) => {
                                if (boxes[idx]) boxes[idx].value = digit
                            })
                            const lastIndex = Math.min(pasted.length - 1, 5)
                            boxes[lastIndex]?.focus()
                        })
                    })
                },
                preConfirm: () => {
                    const boxes = Array.from({ length: 6 }, (_, i) => document.getElementById(`admin-otp-box-${i}`))
                    const value = boxes.map(b => b.value).join('')
                    if (!/^\d{6}$/.test(value)) {
                        Swal.showValidationMessage('Enter the 6-digit code')
                        return false
                    }
                    return value
                }
            })

            if (isDenied) {
                try {
                    await sendOnce()
                } catch (err) {
                    await Swal.fire({ icon: 'error', title: 'Resend Failed', text: err.message, confirmButtonColor: '#FF6F3C' })
                }
                continue
            }

            if (!isConfirmed) return false

            const { data: matches, error: verifyError } = await supabase
                .from('admin_otp_codes')
                .select('id, expires_at')
                .eq('email', admin.email)
                .eq('otp', otp)
                .eq('verified', false)
                .order('created_at', { ascending: false })
                .limit(1)

            const match = matches?.[0]

            if (verifyError || !match || new Date(match.expires_at) < new Date()) {
                await Swal.fire({ icon: 'error', title: 'Verification Failed', text: verifyError?.message || 'Invalid or expired code', confirmButtonColor: '#FF6F3C' })
                continue
            }

            await supabase.from('admin_otp_codes').update({ verified: true }).eq('id', match.id)

            return true
        }
    }

    // ── Admin PIN save — delegates to the backend, which verifies the caller's
    // identity and role server-side from the JWT and writes with service-role,
    // bypassing the missing/unsafe RLS UPDATE policy on staff_accounts.
    const handleSaveAdminPin = async (e) => {
        e.preventDefault()

        if (isSavingPinRef.current) return

        if (!isAdminStaff) {
            Swal.fire({
                icon: 'warning',
                title: 'Access Denied',
                text: 'Only staff members with the Admin role can manage PIN settings.',
                confirmButtonColor: '#FF6F3C'
            })
            return
        }

        const validationError = validateForm()
        if (validationError) {
            Swal.fire({ icon: 'warning', title: 'Invalid Input', text: validationError, confirmButtonColor: '#FF6F3C' })
            return
        }

        const confirmResult = await Swal.fire({
            title: 'Save Admin PIN?',
            text: 'This will update your admin PIN.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#FF6F3C',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Save PIN',
            cancelButtonText: 'Cancel'
        })
        if (!confirmResult.isConfirmed) return


        if (isSavingPinRef.current) return   // check again post-await
        isSavingPinRef.current = true

        try {
            setSaving(true)

            const verified = await requestAndVerifyAdminOtp(currentStaff)
            if (!verified) return

            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const res = await fetch(`${API_BASE_URL}/api/pos/admin/pin`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ newPin: formData.newPin }),
            })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Failed to save PIN.')

            setFormData({ newPin: '', confirmPin: '' })
            await Swal.fire({ icon: 'success', title: 'PIN Saved', text: 'Your admin PIN has been updated successfully.', confirmButtonColor: '#FF6F3C' })
        } catch (error) {
            console.error('Error saving admin PIN:', error)
            await Swal.fire({ icon: 'error', title: 'Save Failed', text: error.message || 'Failed to update admin PIN.', confirmButtonColor: '#FF6F3C' })
        } finally {
            setSaving(false)
            isSavingPinRef.current = false
        }
    }

    // ── OTP helper: sends the code, prompts for it, verifies it. Returns true/false. ──
    const requestAndVerifyOtp = async (barista, newPin) => {
        const baristaId = barista.barista_id
        const channel = 'email'

        const sendOnce = async () => {
            const { data, error } = await supabase.functions.invoke('barista-otp', {
                body: { action: 'sendOtp', baristaId, newPin, channel }
            })
            if (error || data?.error) {
                let message = data?.error
                if (!message && error) {
                    try { message = (await error.context.json())?.error || error.message } catch (_) { message = error.message }
                }
                throw new Error(message || 'Failed to send code')
            }
        }

        await sendOnce()

        const destinationLabel = 'email'

        while (true) {
            const { value: otp, isConfirmed, isDenied } = await Swal.fire({
                title: 'Enter Verification Code',
                text: `We sent a 6-digit code to your registered ${destinationLabel}.`,
                html: `
                    <div id="otp-container" style="display:flex; gap:8px; justify-content:center; margin-top:12px;">
                        ${Array.from({ length: 6 }).map((_, i) => `
                            <input
                                id="otp-box-${i}"
                                type="text"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                maxlength="1"
                                autocomplete="one-time-code"
                                style="width:48px; height:56px; text-align:center; font-size:24px; font-weight:bold; border:2px solid #d1d5db; border-radius:8px; outline:none;"
                            />
                        `).join('')}
                    </div>
                `,
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: 'Verify',
                denyButtonText: 'Resend Code',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#FF6F3C',
                denyButtonColor: '#6B7280',
                didOpen: () => {
                    const boxes = Array.from({ length: 6 }, (_, i) => document.getElementById(`otp-box-${i}`))
                    boxes[0]?.focus()

                    boxes.forEach((box, i) => {
                        box.addEventListener('input', (e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 1)
                            e.target.value = val
                            if (val && i < 5) boxes[i + 1]?.focus()
                        })
                        box.addEventListener('keydown', (e) => {
                            if (e.key === 'Backspace' && !box.value && i > 0) {
                                boxes[i - 1]?.focus()
                            }
                            if (e.key === 'Enter') {
                                Swal.clickConfirm()
                            }
                        })
                        box.addEventListener('paste', (e) => {
                            e.preventDefault()
                            const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6)
                            pasted.split('').forEach((digit, idx) => {
                                if (boxes[idx]) boxes[idx].value = digit
                            })
                            const lastIndex = Math.min(pasted.length - 1, 5)
                            boxes[lastIndex]?.focus()
                        })
                    })
                },
                preConfirm: () => {
                    const boxes = Array.from({ length: 6 }, (_, i) => document.getElementById(`otp-box-${i}`))
                    const value = boxes.map(b => b.value).join('')
                    if (!/^\d{6}$/.test(value)) {
                        Swal.showValidationMessage('Enter the 6-digit code')
                        return false
                    }
                    return value
                }
            })

            if (isDenied) {
                try {
                    await sendOnce()
                } catch (err) {
                    await Swal.fire({ icon: 'error', title: 'Resend Failed', text: err.message, confirmButtonColor: '#FF6F3C' })
                }
                continue
            }

            if (!isConfirmed) return false

            const { data: verifyData, error: verifyError } = await supabase.functions.invoke('barista-otp', {
                body: { action: 'verifyOtp', baristaId, otp }
            })

            if (verifyError || verifyData?.error) {
                let message = verifyData?.error
                if (!message && verifyError) {
                    try { message = (await verifyError.context.json())?.error || verifyError.message } catch (_) { message = verifyError.message }
                }
                await Swal.fire({ icon: 'error', title: 'Verification Failed', text: message || 'Invalid code', confirmButtonColor: '#FF6F3C' })
                continue
            }

            return true
        }
    }

    // ── Barista PIN save (now requires SMS OTP verification before the PIN is written) ──
    const handleSaveBaristaPin = async (e) => {
        e.preventDefault()

        if (!selectedBarista?.barista_id) {
            Swal.fire({
                icon: 'warning',
                title: 'No Barista Selected',
                text: 'Please select your name from the sidebar before setting up a PIN.',
                confirmButtonColor: '#FF6F3C'
            })
            return
        }

        const validationError = validateForm()
        if (validationError) {
            Swal.fire({ icon: 'warning', title: 'Invalid Input', text: validationError, confirmButtonColor: '#FF6F3C' })
            return
        }

        const confirmResult = await Swal.fire({
            title: 'Save Your PIN?',
            text: `We'll text a verification code to confirm it's really ${selectedBarista.first_name} ${selectedBarista.last_name} before saving.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#FF6F3C',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Send Code',
            cancelButtonText: 'Cancel'
        })
        if (!confirmResult.isConfirmed) return

        try {
            setSaving(true)
            const verified = await requestAndVerifyOtp(selectedBarista, formData.newPin)
            if (!verified) return

            setFormData({ newPin: '', confirmPin: '' })
            await Swal.fire({
                icon: 'success',
                title: 'PIN Saved',
                text: `PIN for ${selectedBarista.first_name} ${selectedBarista.last_name} has been updated successfully.`,
                confirmButtonColor: '#FF6F3C'
            })
        } catch (error) {
            console.error('Error saving barista PIN:', error)
            await Swal.fire({ icon: 'error', title: 'Save Failed', text: error.message || 'Failed to update PIN.', confirmButtonColor: '#FF6F3C' })
        } finally {
            setSaving(false)
        }
    }

    /* 
    const fetchBranches = async () => {
        try {
            const { data, error } = await supabase
                .from('branches')
                .select('branch_id, name')
                .neq('status', 'inactive')
                .order('name', { ascending: true })
            if (error) throw error
            setBranches(data || [])
        } catch (error) {
            console.error('Error fetching branches:', error)
            await Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load branches.', confirmButtonColor: '#FF6F3C' })
        }
    }

    const fetchBranchPaymentMethods = async (branchId) => {
        try {
            setLoadingPaymentMethods(true)
            const { data, error } = await supabase
                .from('branch_payment_methods')
                .select('payment_method, is_enabled')
                .eq('branch_id', branchId)
            if (error) throw error

            const existing = data || []
            const merged = PAYMENT_METHODS.map((method) => {
                const found = existing.find((row) => row.payment_method === method.id)
                return { ...method, is_enabled: found ? found.is_enabled : false }
            })
            setBranchPaymentMethods(merged)
        } catch (error) {
            console.error('Error fetching branch payment methods:', error)
            await Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load payment methods for this branch.', confirmButtonColor: '#FF6F3C' })
        } finally {
            setLoadingPaymentMethods(false)
        }
    }

    const handleTogglePaymentMethod = (methodId) => {
        setBranchPaymentMethods((prev) =>
            prev.map((method) => method.id === methodId ? { ...method, is_enabled: !method.is_enabled } : method)
        )
    }

    const handleSavePaymentMethods = async () => {
        if (!selectedBranchId) {
            Swal.fire({ icon: 'warning', title: 'No Branch Selected', text: 'Please select a branch first.', confirmButtonColor: '#FF6F3C' })
            return
        }

        const enabledCount = branchPaymentMethods.filter((m) => m.is_enabled).length
        if (enabledCount === 0) {
            const confirmResult = await Swal.fire({
                title: 'No Payment Methods Enabled',
                text: 'This branch will not be able to accept any orders if no payment method is enabled. Save anyway?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#FF6F3C',
                cancelButtonColor: '#6B7280',
                confirmButtonText: 'Save Anyway',
                cancelButtonText: 'Cancel'
            })
            if (!confirmResult.isConfirmed) return
        }

        try {
            setSavingPaymentMethods(true)
            const rows = branchPaymentMethods.map((method) => ({
                branch_id: selectedBranchId,
                payment_method: method.id,
                is_enabled: method.is_enabled,
                updated_by: currentStaff?.userType === 'admin' ? currentStaff.id : null
            }))

            const { error } = await supabase
                .from('branch_payment_methods')
                .upsert(rows, { onConflict: 'branch_id,payment_method' })
            if (error) throw error

            await Swal.fire({ icon: 'success', title: 'Saved', text: 'Payment methods updated successfully.', confirmButtonColor: '#FF6F3C' })
        } catch (error) {
            console.error('Error saving branch payment methods:', error)
            await Swal.fire({ icon: 'error', title: 'Save Failed', text: error.message || 'Failed to save payment methods.', confirmButtonColor: '#FF6F3C' })
        } finally {
            setSavingPaymentMethods(false)
        }
    }
     */

    if (loading) {
        return (
            <div className="flex-1 overflow-y-auto bg-gray-50">
                <div className="p-4 sm:p-8">
                    <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
                        Loading settings...
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="p-4 sm:p-8">
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900">General Settings</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Manage account-related settings and security options.
                            </p>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-600">
                                No additional settings are available for this account yet.
                            </p>
                        </div>
                    </div>

                    {/* ── Admin PIN card ── */}
                    {isAdminStaff && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-6 py-5 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-900">Admin PIN</h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    Set a 4 to 6 digit PIN for admin approval actions such as void authorization.
                                </p>
                            </div>

                            <form onSubmit={handleSaveAdminPin} className="p-6 space-y-6">
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                    <p className="text-sm text-amber-800">
                                        Temporary setup: this PIN can be saved without password verification for development and POS void testing.
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            New PIN <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            name="newPin"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={formData.newPin}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent"
                                            placeholder="4 to 6 digits"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Confirm PIN <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            name="confirmPin"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={formData.confirmPin}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent"
                                            placeholder="Re-enter PIN"
                                        />
                                    </div>
                                </div>
                                <div className="pt-4 border-t border-gray-200 flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-6 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {saving ? 'Saving...' : 'Save PIN'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* 
                    {isAdminStaff && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-6 py-5 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-900">Branch Payment Methods</h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    Choose which payment methods each branch accepts. Mobile app checkout only shows enabled methods.
                                </p>
                            </div>
                            <div className="p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Branch <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={selectedBranchId}
                                        onChange={(e) => setSelectedBranchId(e.target.value)}
                                        className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent"
                                    >
                                        <option value="">Select a branch...</option>
                                        {branches.map((branch) => (
                                            <option key={branch.branch_id} value={branch.branch_id}>{branch.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {selectedBranchId && (
                                    loadingPaymentMethods ? (
                                        <p className="text-sm text-gray-500">Loading payment methods...</p>
                                    ) : (
                                        <>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                {branchPaymentMethods.map((method) => (
                                                    <label key={method.id} className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                                                        <input
                                                            type="checkbox"
                                                            checked={method.is_enabled}
                                                            onChange={() => handleTogglePaymentMethod(method.id)}
                                                            className="h-4 w-4 text-[#FF6F3C] rounded border-gray-300 focus:ring-[#A0826D]"
                                                        />
                                                        <span className="text-sm font-medium text-gray-700">{method.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            <div className="pt-4 border-t border-gray-200 flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={handleSavePaymentMethods}
                                                    disabled={savingPaymentMethods}
                                                    className="px-6 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {savingPaymentMethods ? 'Saving...' : 'Save Payment Methods'}
                                                </button>
                                            </div>
                                        </>
                                    )
                                )}
                            </div>
                        </div>
                    )}
                    */}

                    {/* ── Barista PIN card ── */}
                    {isBaristaAccount && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-6 py-5 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-900">Your PIN</h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    Set a 4 to 6 digit personal PIN used to identify you on the POS.
                                </p>
                            </div>

                            {!selectedBarista ? (
                                <div className="p-6">
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                        <p className="text-sm text-amber-800">
                                            Select your name from the sidebar first — the PIN is tied to your individual barista profile, not the shared device login.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSaveBaristaPin} className="p-6 space-y-6">
                                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                        <p className="text-sm text-orange-800">
                                            Setting up PIN for <strong>{selectedBarista.first_name} {selectedBarista.last_name}</strong>. Switch names in the sidebar if this isn't you.
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                New PIN <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                name="newPin"
                                                inputMode="numeric"
                                                maxLength={6}
                                                value={formData.newPin}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent"
                                                placeholder="4 to 6 digits"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Confirm PIN <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                name="confirmPin"
                                                inputMode="numeric"
                                                maxLength={6}
                                                value={formData.confirmPin}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent"
                                                placeholder="Re-enter PIN"
                                            />
                                        </div>
                                    </div>
                                    <div className="pt-4 border-t border-gray-200 flex justify-end">
                                        <button
                                            type="submit"
                                            disabled={saving}
                                            className="px-6 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {saving ? 'Saving...' : 'Save PIN'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    )}

                    {!isAdminStaff && !isBaristaAccount && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-6 py-5 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-900">PIN Settings</h2>
                            </div>
                            <div className="p-6">
                                <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                                    <p className="text-sm text-red-700">
                                        No additional settings are available for this account yet.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}